import React, { useCallback, useState } from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  Flex,
  Select,
  Spinner,
  Text,
} from '@chakra-ui/react';
import ProjectRecordApi from '~/common/api/zeiterfassungServerApi/ProjectRecordApi';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import { formatName } from '~/common/utils/employeeUtils';
import { ProjectRecordsList } from './ProjectRecordsList';
import useTranslate from '~/common/utils/translate';

interface Props {
  start: string;
  end: string;
  projectId: number;
  recordCount: number;
}

export const ProjectRecordsCollapsible: React.FC<Props> = ({
  start,
  end,
  projectId,
  recordCount,
}) => {
  const t = useTranslate();
  const [isOpen, setIsOpen] = useState(false);
  const [employeeFilter, setEmployeeFilter] = useState<number | null>(null);
  const [onlyUnseen, setOnlyUnseen] = useState(false);

  const { data, isLoading } = ProjectRecordApi.hooks.useProjectRecords({
    start,
    end,
    project: projectId,
    employee: employeeFilter ?? undefined,
    only_unseen: onlyUnseen,
  });

  const { mutate: markSeen, variables: markingId } =
    ProjectRecordApi.hooks.useMarkProjectRecordSeen();

  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const getEmployeeName = useCallback(
    (employeeId: number) => {
      const emp = employees?.find((e) => Number(e.id) === employeeId);
      return emp ? formatName(emp) : undefined;
    },
    [employees],
  );

  return (
    <Box
      bg="gray.50"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      p={3}
    >
      <Flex
        justify="space-between"
        align="center"
        cursor="pointer"
        onClick={() => setIsOpen((v) => !v)}
      >
        <Text fontSize="sm" fontWeight="600">
          {isOpen ? '▼' : '▶'} {t('projectRecords.records')}
        </Text>
        <Text fontSize="xs" color="gray.500">
          {recordCount} {t('projectRecords.recordCount')}
        </Text>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <Box mt={3}>
          <Flex gap={3} mb={3} align="center">
            <Select
              placeholder={t('projectRecords.filterEmployee')}
              size="sm"
              maxW="200px"
              value={employeeFilter ?? ''}
              onChange={(e) =>
                setEmployeeFilter(e.target.value ? Number(e.target.value) : null)
              }
            >
              {employees?.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {formatName(emp)}
                </option>
              ))}
            </Select>
            <Checkbox
              isChecked={onlyUnseen}
              onChange={(e) => setOnlyUnseen(e.target.checked)}
              size="sm"
            >
              {t('projectRecords.onlyUnseen')}
            </Checkbox>
          </Flex>
          {isLoading && <Spinner size="sm" />}
          {!isLoading && data && data.length > 0 && (
            <ProjectRecordsList
              records={data}
              onMarkSeen={(id) => markSeen(id)}
              markingId={markingId}
              getEmployeeName={getEmployeeName}
            />
          )}
          {!isLoading && data && data.length === 0 && (
            <Text fontSize="sm" color="gray.500">
              {t('projectRecords.empty')}
            </Text>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
